import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as fabric from 'fabric';
import type { Canvas as FabricCanvas } from 'fabric';

export interface ExportOptions {
  format: 'png' | 'jpeg' | 'pdf';
  quality?: number; // 0-1 for JPEG quality
  backgroundColor?: string;
  includeQuestionBackground?: boolean;
  scale?: number; // Export scale factor
  filename?: string;
}

export interface BatchExportOptions extends ExportOptions {
  questions: Array<{
    questionId: string;
    questionOrder: number;
  }>;
  onProgress?: (current: number, total: number) => void;
}

export class ExportService {
  /**
   * Clone canvas without CORS-restricted images
   */
  private static async cloneCanvasWithoutCORS(
    originalCanvas: FabricCanvas,
    backgroundColor: string
  ): Promise<HTMLCanvasElement> {
    // Create a new canvas element
    const canvas = document.createElement('canvas');
    canvas.width = originalCanvas.width!;
    canvas.height = originalCanvas.height!;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas context');
    
    // Fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Get the underlying canvas element from fabric
    const fabricCanvasEl = originalCanvas.getElement();
    
    try {
      // Try to draw the entire fabric canvas
      ctx.drawImage(fabricCanvasEl, 0, 0);
    } catch (e) {
      // If that fails due to CORS, render objects individually
      
      // Set up a temporary clean fabric canvas
      const tempEl = document.createElement('canvas');
      tempEl.width = canvas.width;
      tempEl.height = canvas.height;
      const tempFabric = new fabric.Canvas(tempEl) as FabricCanvas;
      tempFabric.backgroundColor = backgroundColor;
      
      // Copy objects one by one
      const objects = originalCanvas.getObjects();
      for (const obj of objects) {
        if (obj.type === 'image') {
          // Skip images that might have CORS issues
          continue;
        }
        
        try {
          // Clone non-image objects
          const cloned = await new Promise<fabric.Object>((resolve, reject) => {
            (obj as any).clone((clonedObj: fabric.Object) => {
              resolve(clonedObj);
            });
          });
          tempFabric.add(cloned);
        } catch (err) {
        }
      }
      
      // Render the temp canvas
      tempFabric.renderAll();
      
      // Draw to our output canvas
      ctx.drawImage(tempEl, 0, 0);
      
      // Clean up
      tempFabric.dispose();
    }
    
    return canvas;
  }

  /**
   * Export the current canvas content
   */
  static async exportCanvas(
    canvas: FabricCanvas,
    options: ExportOptions
  ): Promise<void> {
    const {
      format,
      quality = 0.9,
      backgroundColor = 'white',
      includeQuestionBackground = true,
      scale = 2,
      filename
    } = options;

    // Generate filename if not provided
    const defaultFilename = `tahta-export-${new Date().getTime()}`;
    const finalFilename = filename || defaultFilename;

    try {
      if (format === 'pdf') {
        await this.exportAsPDF(canvas, finalFilename, scale, backgroundColor);
      } else {
        await this.exportAsImage(canvas, format, finalFilename, quality, scale, backgroundColor);
      }
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error('Export işlemi başarısız oldu');
    }
  }

  /**
   * Export canvas as PNG or JPEG image
   */
  private static async exportAsImage(
    canvas: FabricCanvas,
    format: 'png' | 'jpeg',
    filename: string,
    quality: number,
    scale: number,
    backgroundColor: string
  ): Promise<void> {
    let dataURL: string;
    
    try {
      // Get canvas data URL
      dataURL = canvas.toDataURL({
        format,
        quality,
        multiplier: scale,
        left: 0,
        top: 0,
        width: canvas.width,
        height: canvas.height,
        // @ts-ignore - fabric.js type issue
        backgroundColor
      });
    } catch (error) {
      // If CORS error (SecurityError with tainted canvas message)
      if (error instanceof Error && 
          (error.name === 'SecurityError' || error.message.includes('tainted') || error.message.includes('Tainted'))) {
        
        // Clone canvas without cross-origin images
        const clonedCanvas = await this.cloneCanvasWithoutCORS(canvas, backgroundColor);
        dataURL = clonedCanvas.toDataURL('image/' + format, quality);
      } else {
        throw error;
      }
    }

    // Create download link
    const link = document.createElement('a');
    link.download = `${filename}.${format}`;
    link.href = dataURL;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Export canvas as PDF
   */
  private static async exportAsPDF(
    canvas: FabricCanvas,
    filename: string,
    scale: number,
    backgroundColor: string
  ): Promise<void> {
    // Get canvas dimensions
    const width = canvas.width || 800;
    const height = canvas.height || 600;

    // Create PDF with appropriate orientation
    const orientation = width > height ? 'landscape' : 'portrait';
    const pdf = new jsPDF({
      orientation,
      unit: 'px',
      format: [width, height]
    });

    let imgData: string;
    
    try {
      // Convert canvas to image data
      imgData = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: scale,
        // @ts-ignore - fabric.js type issue
        backgroundColor
      });
    } catch (error) {
      // If CORS error (SecurityError with tainted canvas message)
      if (error instanceof Error && 
          (error.name === 'SecurityError' || error.message.includes('tainted') || error.message.includes('Tainted'))) {
        const clonedCanvas = await this.cloneCanvasWithoutCORS(canvas, backgroundColor);
        imgData = clonedCanvas.toDataURL();
      } else {
        throw error;
      }
    }

    // Add image to PDF
    pdf.addImage(imgData, 'PNG', 0, 0, width, height);

    // Save PDF
    pdf.save(`${filename}.pdf`);
  }

  /**
   * Export multiple questions as separate files or combined PDF
   */
  static async batchExport(
    canvasGetter: (questionId: string) => Promise<FabricCanvas | null>,
    options: BatchExportOptions
  ): Promise<void> {
    const { questions, onProgress, format, ...exportOptions } = options;
    const results: Array<{ questionId: string; success: boolean; error?: string }> = [];

    if (format === 'pdf') {
      // Create a single PDF with all questions
      await this.batchExportAsPDF(canvasGetter, questions, exportOptions, onProgress);
    } else {
      // Export each question as a separate image
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        
        try {
          const canvas = await canvasGetter(question.questionId);
          if (!canvas) {
            throw new Error('Canvas bulunamadı');
          }

          const filename = `${exportOptions.filename || 'tahta'}-soru-${question.questionOrder}`;
          await this.exportCanvas(canvas, {
            ...exportOptions,
            format,
            filename
          });

          results.push({ questionId: question.questionId, success: true });
        } catch (error) {
          results.push({ 
            questionId: question.questionId, 
            success: false, 
            error: error instanceof Error ? error.message : 'Bilinmeyen hata' 
          });
        }

        if (onProgress) {
          onProgress(i + 1, questions.length);
        }
      }
    }

    // Report any failures
    const failures = results.filter(r => !r.success);
    if (failures.length > 0) {
      console.error('Export failures:', failures);
    }
  }

  /**
   * Export multiple questions as a single PDF
   */
  private static async batchExportAsPDF(
    canvasGetter: (questionId: string) => Promise<FabricCanvas | null>,
    questions: Array<{ questionId: string; questionOrder: number }>,
    options: Omit<ExportOptions, 'format'>,
    onProgress?: (current: number, total: number) => void
  ): Promise<void> {
    const { scale = 2, backgroundColor = 'white', filename } = options;
    
    // Get first canvas to determine dimensions
    const firstCanvas = await canvasGetter(questions[0].questionId);
    if (!firstCanvas) {
      throw new Error('İlk soru yüklenemedi');
    }

    const width = firstCanvas.width || 800;
    const height = firstCanvas.height || 600;
    const orientation = width > height ? 'landscape' : 'portrait';

    // Create PDF
    const pdf = new jsPDF({
      orientation,
      unit: 'px',
      format: [width, height]
    });

    // Add each question to PDF
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      
      try {
        const canvas = await canvasGetter(question.questionId);
        if (!canvas) {
          continue;
        }

        // Add new page for each question (except first)
        if (i > 0) {
          pdf.addPage();
        }

        // Convert canvas to image
        const imgData = canvas.toDataURL({
          format: 'png',
          quality: 1,
          multiplier: scale,
          // @ts-ignore - fabric.js type issue
          backgroundColor
        });

        // Add image to PDF
        pdf.addImage(imgData, 'PNG', 0, 0, width, height);

        if (onProgress) {
          onProgress(i + 1, questions.length);
        }
      } catch (error) {
        console.error(`Soru ${question.questionOrder} export hatası:`, error);
      }
    }

    // Save PDF
    const finalFilename = filename || `tahta-test-export-${new Date().getTime()}`;
    pdf.save(`${finalFilename}.pdf`);
  }

  /**
   * Export canvas using html2canvas (alternative method for complex content)
   */
  static async exportWithHtml2Canvas(
    element: HTMLElement,
    options: ExportOptions
  ): Promise<void> {
    const { format, quality = 0.9, scale = 2, filename } = options;

    try {
      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        allowTaint: false
      });

      if (format === 'pdf') {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [canvas.width, canvas.height]
        });
        
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
        pdf.save(`${filename || 'tahta-export'}.pdf`);
      } else {
        // Convert to blob and download
        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${filename || 'tahta-export'}.${format}`;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          },
          format === 'png' ? 'image/png' : 'image/jpeg',
          quality
        );
      }
    } catch (error) {
      console.error('html2canvas export failed:', error);
      throw new Error('Export işlemi başarısız oldu');
    }
  }
}